import { useTheme } from '@emotion/react';
import is from '@sindresorhus/is';
import { type FunctionComponent, useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';

import type { JobItemDetails } from '../../api/use-job-item-details';
import { useJobItemVersions } from '../../api/use-job-item-versions';
import { CompareVersionButton } from './CompareVersionButton';
import { ButtonContainer, Container, Text, TextContainer, variants } from './notification-bar-styled';
import { getPublishedVersionInfo, getPublisherNotificationKey } from './notification-version-utils';
import { VersionPublishButton } from './VersionPublishButton';

export const PublisherNotification: FunctionComponent<{ readonly item: JobItemDetails }> = ({ item }) => {
    const { translate } = useContext(ApplicationContext);
    const theme = useTheme();
    const { data: versions } = useJobItemVersions({
        architectureId: item.architectureId,
        itemId: item.id,
        itemType: item.type,
    });

    if (item.actions.canPublish !== true) {
        return null;
    }

    const jobTypeKey =
        item.type === 'job-role' ? 'pages.job-architecture-v2.job-role' : 'pages.job-architecture-v2.job-level';
    const jobType = translate(jobTypeKey);

    const currentVersion = item.versionInfo?.version.toString();
    const { hasPreviousPublishedVersion, lastPublishedVersion } = getPublishedVersionInfo(versions);

    const notificationKey = getPublisherNotificationKey(hasPreviousPublishedVersion);

    const canShowCompareButton =
        !is.nullOrUndefined(lastPublishedVersion) &&
        !is.nullOrUndefined(currentVersion) &&
        !is.emptyStringOrWhitespace(currentVersion);

    return (
        <Container
            key="publisher"
            animate={variants.animate}
            exit={variants.exit}
            initial={variants.initial}
            style={{ backgroundColor: theme.colors.success[25], borderColor: theme.colors.success[100] }}
        >
            <TextContainer style={{ color: theme.colors.success[600] }}>
                <Text>{translate(notificationKey, { jobType })}</Text>
            </TextContainer>
            <ButtonContainer>
                {canShowCompareButton && (
                    <CompareVersionButton
                        currentVersion={currentVersion}
                        item={item}
                        lastPublishedVersion={lastPublishedVersion}
                    />
                )}
                <VersionPublishButton item={item} />
            </ButtonContainer>
        </Container>
    );
};
